import { pushNotificationAction } from 'actions/app-actions';
import { SCRIBE_REGULATION_EVENTS } from 'applications/scribe/instrumentations/track';
import cx from 'classnames';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import usePrevious from 'common/usePrevious';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import canCreateDelivery from 'components/v2/anytime-booking/common/can-create-delivery';
import useCreatingDeliveriesResult from 'components/v2/anytime-booking/common/use-creating-deliveries-result';
import ClickOnce from 'components/v2/click-once';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { getDeliveryErrors, getErrorMessage } from 'epics/v2/common-errors';
import { Button } from 'grab-ui';
import { getDuplicatedDeliveryIDs } from 'helper/anytime-booking';
import { isAreaZoningError } from 'helper/error-codes';
import { getQuoteAmountDefaultTextFromCurrencyCode } from 'helper/quote';
import I18n from 'i18n-js';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _some from 'lodash/some';
import moment from 'moment';
import PropTypes from 'prop-types';
import pressedCursor from 'public/images/pressed-cursor.png';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import {
  enableItemTypeFreeTextSelector,
  isItemTypeMandatorySelector,
} from 'selectors/app-selector';
import {
  allDeliveriesSelector,
  creatingDeliveriesSelector,
} from 'selectors/new-booking-selectors';
import ExpireQuoteModal from './../expire-quote-modal/expire-quote-modal';
import OfferWarningModal from './../offer-warning-modal/offer-warning-modal';
import DeliveryQuote from './delivery-quote';
import styles from './styles.less';

const hasSomeOfferWarning = (allDeliveries, createDeliveryIDs) =>
  _some(allDeliveries, delivery => {
    if (createDeliveryIDs.includes(delivery.id)) {
      const discountEligibilityError = _get(
        delivery,
        'detail.configuration.offerConfig.discountEligibilityError',
      );
      return !!discountEligibilityError;
    }
    return false;
  });

const getExpiredDeliveryIDs = (allDeliveries, createDeliveryIDs) => {
  if (!createDeliveryIDs || !allDeliveries) {
    return [];
  }
  return createDeliveryIDs.filter(deliveryID => {
    const delivery = allDeliveries.find(delivery => delivery.id === deliveryID);
    const expireAt = _get(delivery, 'quote.expireAt');
    if (expireAt) {
      return moment().isAfter(moment(expireAt));
    }
    return false;
  });
};

const QuotePanel = ({
  history,
  dispatch,
  creatingDeliveries,
  allDeliveries,
  formRefs,
}) => {
  const [showOfferWarningModal, setShowOfferWarningModal] = useState(false);
  const [showExpireQuoteModal, setShowExpireQuoteModal] = useState(false);
  const [expiredDeliveryIDs, setExpiredDeliveryIDs] = useState(undefined);
  const [
    offerModalCreateDeliveryIDs,
    setOfferModalCreateDeliveryIDs,
  ] = useState([]);

  const [triggerType, setTriggerType] = useState('');

  const isItemTypeMandatory = useSelector(isItemTypeMandatorySelector);
  const enableItemTypeFreeText = useSelector(enableItemTypeFreeTextSelector);

  const totalPriceTag = useMemo(
    () => {
      const totalAmount = allDeliveries.reduce(
        (totalAmount, delivery) =>
          totalAmount + _get(delivery, 'quote.finalFare.lowerBound', 0),
        0,
      );
      const currency = _get(allDeliveries[0], 'quote.currency');
      const code = _get(currency, 'code');
      return totalAmount > 0
        ? formatCurrency({ code, amount: calcPrice(totalAmount, currency) })
        : getQuoteAmountDefaultTextFromCurrencyCode(code);
    },
    [allDeliveries],
  );

  const [preventDuplicate, setPreventDuplicate] = useState(false);

  const canCreateDeliveries = useMemo(
    () =>
      !creatingDeliveries &&
      _every(allDeliveries, delivery =>
        canCreateDelivery(delivery, formRefs[delivery.id], {
          isItemTypeMandatory,
          enableItemTypeFreeText,
        }),
      ),
    [
      creatingDeliveries,
      allDeliveries,
      formRefs,
      isItemTypeMandatory,
      enableItemTypeFreeText,
    ],
  );

  const onCloseOfferWarningModal = useCallback(
    () => {
      setShowOfferWarningModal(false);
    },
    [setShowOfferWarningModal],
  );

  const onCloseExpireQuoteModal = useCallback(
    () => {
      if (expiredDeliveryIDs) {
        expiredDeliveryIDs.forEach(deliveryID => {
          dispatch(NewBookingDuck.creators.fetchDeliveryQuote(deliveryID));
        });
      }
      setShowExpireQuoteModal(false);
    },
    [setShowExpireQuoteModal, allDeliveries, expiredDeliveryIDs],
  );

  const onSubmitOfferWarningModal = useCallback(
    () => {
      const createDeliveryIDs = offerModalCreateDeliveryIDs;
      let needFetchDeliveryQuote = false;
      allDeliveries.forEach(delivery => {
        if (createDeliveryIDs.includes(delivery.id)) {
          const discountEligibilityError = _get(
            delivery,
            'detail.configuration.offerConfig.discountEligibilityError',
          );
          if (discountEligibilityError) {
            dispatch(NewBookingDuck.creators.setOfferConfig(delivery.id, null));
            dispatch(NewBookingDuck.creators.fetchDeliveryQuote(delivery.id));
            needFetchDeliveryQuote = true;
          }
        }
      });

      if (needFetchDeliveryQuote) {
        setTriggerType('CREATE_DELIVERY');
      }

      setShowOfferWarningModal(false);
    },
    [
      dispatch,
      setShowOfferWarningModal,
      offerModalCreateDeliveryIDs,
      allDeliveries,
      onCreateDeliveries,
    ],
  );

  const someFetchingQuote = useMemo(
    () =>
      _some(allDeliveries, delivery => _get(delivery, 'fetchingQuote', false)),
    [allDeliveries],
  );
  const previousSomeFetchingQuote = usePrevious(someFetchingQuote);

  useEffect(
    () => {
      if (triggerType === 'CREATE_DELIVERY') {
        if (
          !someFetchingQuote &&
          someFetchingQuote !== previousSomeFetchingQuote
        ) {
          setTriggerType('');
          onCreateDeliveries();
        }
      }
    },
    [
      triggerType,
      onCreateDeliveries,
      someFetchingQuote,
      previousSomeFetchingQuote,
    ],
  );

  const onCreateDeliveries = useCallback(
    (deliveryIDs, skipDuplicateBookingCheckDeliveryIDs = undefined) => {
      // prevent double click
      setPreventDuplicate(true);
      setTimeout(() => {
        setPreventDuplicate(false);
      }, 1000);

      const createDeliveryIDs =
        deliveryIDs && deliveryIDs.length > 0
          ? deliveryIDs
          : allDeliveries.map(delivery => delivery.id);

      const hasOfferWarning = hasSomeOfferWarning(
        allDeliveries,
        createDeliveryIDs,
      );
      if (hasOfferWarning) {
        setOfferModalCreateDeliveryIDs(createDeliveryIDs);
        setShowOfferWarningModal(true);
        return;
      }

      const expiredDeliveryIDs = getExpiredDeliveryIDs(
        allDeliveries,
        createDeliveryIDs,
      );
      if (expiredDeliveryIDs && expiredDeliveryIDs.length > 0) {
        setExpiredDeliveryIDs(expiredDeliveryIDs);
        setShowExpireQuoteModal(true);
        return;
      }

      Promise.all(
        createDeliveryIDs
          .map(deliveryID => formRefs[deliveryID])
          .map(form => form.validateFields()),
      )
        .then(() => {
          dispatch(
            NewBookingDuck.creators.createDeliveries({
              deliveryIDs: createDeliveryIDs,
              skipDuplicateBookingCheckDeliveryIDs,
            }),
          );
          dispatch(
            ScribeDuck.creators.scribeExpressDeliveryDetailBook(
              createDeliveryIDs,
            ),
          );
        })
        .catch(error => {
          // eslint-disable-next-line
          console.log('form error', error);
        });
    },
    [setPreventDuplicate, allDeliveries, formRefs],
  );

  const createDeliveriesCallback = useCallback(
    ({ successfulDeliveryIDs, failedDeliveryIDs, error, deliveryErrors }) => {
      dispatch(
        NewBookingDuck.creators.removeSuccessfulDeliveries(
          successfulDeliveryIDs,
        ),
      );
      const successfulCount = _get(successfulDeliveryIDs, 'length', 0);
      const failedCount = _get(failedDeliveryIDs, 'length', 0);
      const totalCount = successfulCount + failedCount;
      if (!error && failedCount === 0) {
        const message =
          successfulCount > 1
            ? I18n.t(
                'v2.homepage.manualOrder.notification.createOrderSuccessful.title.other',
                { amt: successfulCount },
              )
            : I18n.t(
                'v2.homepage.manualOrder.notification.createOrderSuccessful.title.one',
              );
        const description =
          I18n.t(
            'v2.homepage.manualOrder.notification.createOrderSuccessful.description',
          ) +
          I18n.t(
            'v2.homepage.manualOrder.notification.createOrderSuccessful.descriptionLink',
          );
        history.push('/bookings');
        dispatch(
          pushNotificationAction({
            id: 'successfulCreation',
            type: NOTIFICATION_TYPE.CONFIRMED,
            message,
            description,
          }),
        );
      } else {
        if (deliveryErrors) {
          Object.values(deliveryErrors).forEach(error => {
            if (error && isAreaZoningError(error.errorCode)) {
              dispatch(
                ScribeDuck.creators.scribeEVRegulationEvent({
                  eventName:
                    SCRIBE_REGULATION_EVENTS.EV_REGULATION_SNACKBAR_VIEW,
                  errorCode: error.errorCode,
                  title: error.title || error.message,
                }),
              );
            }
          });
        }
        const duplicatedDeliveryIDs = getDuplicatedDeliveryIDs(deliveryErrors);
        const message =
          totalCount === 1
            ? I18n.t(
                'v2.homepage.manualOrder.notification.createOrderFailed.title.one',
              )
            : successfulCount === 0
              ? I18n.t(
                  'v2.homepage.manualOrder.notification.createOrderFailed.title.zero',
                  { total: totalCount },
                )
              : I18n.t(
                  'v2.homepage.manualOrder.notification.createOrderFailed.title.other',
                  { amt: failedCount, total: totalCount },
                );
        const description = () => (
          <div>
            <p>
              {I18n.t(
                'v2.homepage.manualOrder.notification.createOrderFailed.description',
              )}
              <ClickOnce
                onClick={() =>
                  onCreateDeliveries(failedDeliveryIDs, duplicatedDeliveryIDs)
                }
              >
                {I18n.t(
                  'v2.homepage.manualOrder.notification.createOrderFailed.descriptionLink',
                )}
              </ClickOnce>
            </p>
            {error && <p>Error: {getErrorMessage(error)}</p>}
            {deliveryErrors &&
              getDeliveryErrors(deliveryErrors).map(
                ({ deliveryID, displayText }) => (
                  <p key={deliveryID}>{displayText}</p>
                ),
              )}
          </div>
        );
        dispatch(
          pushNotificationAction({
            id: 'failedCreation',
            type: NOTIFICATION_TYPE.WARNING,
            message,
            description,
          }),
        );
      }
    },
    [dispatch, onCreateDeliveries, history],
  );
  useCreatingDeliveriesResult(createDeliveriesCallback);

  return (
    <div className={styles.quotePanel}>
      <ExpireQuoteModal
        handleClose={onCloseExpireQuoteModal}
        visible={showExpireQuoteModal}
      />
      <OfferWarningModal
        handleClose={onCloseOfferWarningModal}
        handleSubmit={onSubmitOfferWarningModal}
        visible={showOfferWarningModal}
      />
      <div className={styles.title}>
        {I18n.t('v2.homepage.manualOrder.heading.priceBreakdown')}
      </div>
      <div className={styles.quotes}>
        {allDeliveries.map((delivery, index) => (
          <DeliveryQuote key={delivery.id} delivery={delivery} index={index} />
        ))}
      </div>
      <div className={styles.totalPrice}>
        <span> {I18n.t('booking.totalPrice')} </span>
        <span className={styles.priceTag}> {totalPriceTag} </span>
      </div>
      <div className={styles.ctaRow}>
        <Button
          type="primary"
          className={cx(styles.bookingButton, {
            [styles.creatingButton]: creatingDeliveries || preventDuplicate,
          })}
          disabled={!canCreateDeliveries || preventDuplicate}
          onClick={() => onCreateDeliveries()}
        >
          {(preventDuplicate || creatingDeliveries) && (
            <img className={styles.icon} src={pressedCursor} alt="" />
          )}
          {I18n.t('v2.homepage.manualOrder.button.bookMultipleOrder', {
            count: allDeliveries.length,
            amt: allDeliveries.length,
          })}
        </Button>
      </div>
    </div>
  );
};

QuotePanel.propTypes = {
  allDeliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  formRefs: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  creatingDeliveries: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
};

QuotePanel.defaultProps = {};

export default withRouter(
  connect(state => ({
    allDeliveries: allDeliveriesSelector(state),
    creatingDeliveries: creatingDeliveriesSelector(state),
  }))(QuotePanel),
);
